import { Fragment, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Collapse from "@material-ui/core/Collapse";
import Button from "@material-ui/core/Button";

import { InputsSimFormCustomerData } from "../content/InputsSimFormCustomerData";
import { InputsSimFormEnergyBillData } from "../content/InputsSimFormEnergyBillData";
import { SimulationResults } from "../content/SimulationResults";
import { LoadingSpinnerOverlayRegular } from "../../shared/components/UIElements/LoadingSpinnerOverlayRegular";
import { Card } from "../../shared/components/UIElements/Card";
import { ModalError } from "../../shared/components/UIElements/ModalError";
import { ModalSuccess } from "../../shared/components/UIElements/ModalSuccess";
import { ModalInvalid } from "../../shared/components/UIElements/ModalInvalid";
import { ButtonCustomColor } from "../../shared/components/MUIThemed/ButtonCustomColor";
import { useForm } from "../../shared/hooks/formHook";
import { useHttpClient } from "../../shared/hooks/httpHook";
import { removeFormStateProperty } from "../../shared/util/removeFormStateProperties";
import { fetchPlaces } from "../api/placesAPI";
import { getUserFees } from "../api/feesAPI";
import { getUserSettings } from "../api/settingsAPI";
import { simulatePVSystem } from "../api/websiteAPI";
import {
  PlaceData,
  SettingData,
  FeeData,
  SimulationResultsData,
  WebsiteData,
} from "../../shared/data/types";

interface WebsiteSimFormProps {
  loadedWebsite: WebsiteData;
  isMobile: boolean;
  id: string;
}

export const WebsiteSimForm = (props: WebsiteSimFormProps) => {
  const { loadedWebsite, isMobile, id } = props;
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [loadedPlaces, setLoadedPlaces] = useState<PlaceData[]>(null);
  const [loadedFees, setLoadedFees] = useState<FeeData[]>(null);
  const [loadedSettings, setLoadedSettings] = useState<SettingData>(null);
  const [chosenCoordinates, setChosenCoordinates] = useState<{
    lat: string;
    lng: string;
  }>({
    lat: "",
    lng: "",
  });
  const [invalidInputs, setInvalidInputs] = useState<{
    showInvalid: boolean;
    invalidFields: string[];
  }>({
    showInvalid: false,
    invalidFields: [],
  });
  const [loadedResults, setLoadedResults] =
    useState<SimulationResultsData>(null);
  const { isLoading, error, success, sendRequest, clearError, clearSuccess } =
    useHttpClient();
  const { formState, inputHandler, reportInvalid } = useForm({}, true);
  const isAGroup = formState.inputs?.tariffModality?.value
    ?.toString()
    ?.toLowerCase()
    ?.includes("grupo a");

  useEffect(() => {
    fetchPlaces({ sendRequest, setLoadedPlaces });
  }, [sendRequest]);

  useEffect(() => {
    if (loadedWebsite.user) {
      getUserFees({ sendRequest, setLoadedFees, uid: loadedWebsite.user });
    }
  }, [sendRequest, loadedWebsite]);

  useEffect(() => {
    getUserSettings({ sendRequest, setLoadedSettings, uid: id });
  }, [sendRequest]);

  useEffect(() => {
    if (loadedPlaces && formState.inputs?.city?.value) {
      const foundCoordinates = loadedPlaces.find((place) => {
        return place.name === formState.inputs?.city?.value;
      });
      if (foundCoordinates) {
        setChosenCoordinates({
          lat: foundCoordinates.lat.toString(),
          lng: foundCoordinates.lng.toString(),
        });
      }
    } else if (!formState.inputs?.city?.value) {
      setChosenCoordinates({
        lat: "",
        lng: "",
      });
    }
  }, [formState.inputs?.city?.value, loadedPlaces]);

  useEffect(() => {
    if (formState.inputs?.classification?.value === "Verde") {
      removeFormStateProperty(formState, "demandPeak");
      removeFormStateProperty(formState, "demandTariffPeak");
    }
  }, [formState.inputs?.classification?.value]);

  const simulateSystemHandler = () => {
    simulatePVSystem({
      sendRequest,
      formState,
      setLoadedResults,
      loadedFees,
      uid: id,
    });
  };

  const continueHandler = () => {};

  const reportInvalidHandler = () => {
    const whatIsInvalid = reportInvalid(formState.inputs);
    setInvalidInputs({ showInvalid: true, invalidFields: whatIsInvalid });
  };

  const invalidInputsModalCloseHandler = () => {
    setInvalidInputs({ showInvalid: false, invalidFields: [] });
  };

  return (
    <Fragment>
      <ModalError error={error} onClear={clearError} />
      <ModalSuccess
        success={success}
        onClear={clearSuccess}
        message="Simulação realizada com sucesso!"
        dontPush
        reset={continueHandler}
      />
      <ModalInvalid
        open={invalidInputs.showInvalid}
        closeHandler={invalidInputsModalCloseHandler}
        message={invalidInputs.invalidFields.map((field) => {
          return <li key={field + "key"}>{field}</li>;
        })}
      />
      {
        <Fragment>
          <h2
            id="website-form-title"
            style={{ color: loadedWebsite.themeColor }}
            className="website__section-title u-margin-top-lg"
          >
            {loadedWebsite.simulator?.title}
          </h2>
          <p className="website__section-paragraph">
            {loadedWebsite.simulator?.subtitle}
          </p>
          <Collapse in={!loadedResults}>
            <Card cardType="websiteCard">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  simulateSystemHandler();
                }}
                className="simpleForm"
              >
                {(isUpdating ||
                  !loadedPlaces ||
                  !loadedFees ||
                  !loadedSettings ||
                  isLoading) && <LoadingSpinnerOverlayRegular />}
                <InputsSimFormCustomerData
                  inputHandler={inputHandler}
                  formState={formState}
                  chosenCoordinates={chosenCoordinates}
                  loadedPlaces={loadedPlaces}
                  setLoadedPlaces={setLoadedPlaces}
                />
                <InputsSimFormEnergyBillData
                  inputHandler={inputHandler}
                  formState={formState}
                  loadedFees={loadedFees}
                  isAGroup={isAGroup}
                />
                {formState.isValid && (
                  <button
                    style={{ backgroundColor: loadedWebsite.themeColor }}
                    className="btn btn--sm-animation btn--custom-color u-margin-top-md"
                    type="submit"
                  >
                    FAZER SIMULAÇÃO
                  </button>
                )}
                {!formState.isValid && (
                  <button
                    className="btn btn--sm-animation btn--grey u-margin-top-md"
                    onClick={reportInvalidHandler}
                    type="button"
                  >
                    O QUE ESTÁ FALTANDO?
                  </button>
                )}
              </form>
            </Card>
          </Collapse>
          <Collapse in={!!loadedResults}>
            <div className="website__sim-results">
              <SimulationResults
                loadedResults={loadedResults}
                isMobile={isMobile}
                loadedWebsite={loadedWebsite}
              />
            </div>
          </Collapse>
        </Fragment>
      }
    </Fragment>
  );
};
